import * as types from '@/store/mutation-types';
import api from '@/store/api';
import { logAction } from '../logActions';
import { editingAllowed } from '../actions';
import router from '../../router';
import * as treeHelpers from '@/common/treeHelpers';
import * as colourHelpers from '@/common/colourHelpers';
import { findStage } from '@/common/stageHelpers';
import { recalculatePartProperties } from '@/common/partHelpers';
import log from '@/logger';
import { checkPermissions } from '../actionManager';
import moment from 'moment';

export const selectPart = ({ commit, state, dispatch }, part) => {
  const promises = [];
  if (part) {
    var project = state.currentProject;
    var parent = treeHelpers.findParent(project, part._id);
    while (parent && parent.type === 'Stage') {
      commit(types.setStageProperties, {
        updateStage: state.stages[parent._id],
        newStageProperties: { showChildren: true },
      });
      parent = treeHelpers.findParent(project, parent._id);
    }
    if (router.currentRoute.params._id !== part.projectId || router.currentRoute.params.partId !== part._id) {
      router.push({
        name: 'ProjectPlanPart',
        params: { _id: part.projectId, partId: part._id },
        query: router.currentRoute.query,
      });
    }
    if (checkPermissions({ type: 'getPartComments' }, state)) {
      const p = dispatch('getPartComments', { projectId: part.projectId, partId: part._id }).then((comments) => {
        commit(types.setSelectedPartComments, comments);
      });
      promises.push(p);
    }
    if (checkPermissions({ type: 'getTodoItems' }, state)) {
      const p = dispatch('getTodoItems', { projectId: part.projectId, partId: part._id }).then((items) => {
        commit(types.setSelectedPartTodoItems, items);
      });
      promises.push(p);
    }
    if (part.inspectionId && part.inspectionId !== '' && checkPermissions({ type: 'getInspection' }, state)) {
      const p = dispatch('getInspection', part.inspectionId).then((inspection) => {
        commit(types.setSelectedPartInspection, inspection);
      });
      promises.push(p);
    } else {
      commit(types.setSelectedPartInspection, null);
    }
  }

  commit(types.selectDiaryEntry, null);
  commit(types.selectStage, null);
  commit(types.selectPart, part);

  return Promise.all(promises);
};

export const getPart = ({ commit, state }, id) => {
  return api.getPart(id);
};

export const undoDeletePart = async ({ commit, state, dispatch }, { part, parent, index }) => {
  var project = state.currentProject;
  var realPart = state.parts[part._id];
  await dispatch('savePartProperties', {
    updatePart: realPart,
    newPartProperties: { archived: false },
    undoStrategy: 'undo',
  });
  commit(types.addPart, {
    newPart: realPart,
    parent: treeHelpers.findChildInTree(project, parent._id).children,
    index: index,
  });
  // save project!?
};

export const redoDeletePart = async ({ commit, state, dispatch }, { part, parent, index }) => {
  var realPart = state.parts[part._id];
  await dispatch('deletePart', { part: realPart, undoStrategy: 'redo' });
};

export const deletePart = async ({ commit, state }, { part, undoStrategy }) => {
  log.debug('actions:deletePart', part);

  if (!editingAllowed(state)) {
    return;
  }

  var project = state.currentProject;
  var parent = treeHelpers.findParent(project, part._id);
  var index;

  if (parent) {
    index = treeHelpers.indexOfChild(parent.children, part._id);
    commit(types.deletePart, { parent: parent.children, index: index });
  }
  commit(types.appendUndo, { action: 'deletePart', data: { part, parent, index }, undoStrategy: undoStrategy });
  commit(types.selectPart, null);
  logAction({ commit, state }, { project: project, object: part, reason: 'Deleted Part' });
  commit(types.setPartProperties, { updatePart: part, newPartProperties: { archived: true } });
  await api
    .savePart(part)
    .then((savedPart) => {
      commit(types.setPartProperties, { updatePart: part, newPartProperties: savedPart });
    })
    .catch((e) => {
      log.error('partActions:deletePart, saving Part', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });

  return api
    .saveProject(project)
    .then((savedProject) => {
      commit(types.setProjectProperties, { updateProject: project, newProjectProperties: savedProject });
      // treeHelpers.outputTree( project.children, 0);

      return Promise.resolve(project);
    })
    .catch((e) => {
      log.error('partActions:deletePart, saving project', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const undoEmbedPartInStage = async ({ commit, state, dispatch }, { stage, part, oldTitle, undoStrategy }) => {
  var project = state.currentProject;

  var realPart = state.parts[part._id];
  var parent = treeHelpers.findParent(project, stage._id);
  await dispatch('setPartProperties', {
    updatePart: realPart,
    newPartProperties: { title: oldTitle },
    undoStrategy: 'undo',
  });
  await dispatch('projectChildMoved', { movedChild: realPart, parent, beforeChild: stage, undoStrategy: 'undo' });

  await dispatch('deleteStage', { stage: findStage(state, stage._id), undoStrategy: 'undo' });
};

export const redoEmbedPartInStage = async ({ commit, state, dispatch }, { stage, part, undoStrategy }) => {
  var realStage = findStage(state, stage._id);
  var realPart = state.parts[part._id];
  realStage = await dispatch('saveStageProperties', {
    updateStage: realStage,
    newStageProperties: { archived: false },
    undoStrategy: 'redo',
  });
  await dispatch('embedPartInStage', { part: realPart, stage: realStage, undoStrategy: 'redo' });
};

export const embedPartInStage = ({ commit, state }, { part, undoStrategy, stage }) => {
  log.debug('actions:embedPartInStage', part);

  if (!editingAllowed(state)) {
    return;
  }

  var oldTitle = part.title;
  var project = state.currentProject;
  return (stage ? Promise.resolve(stage) : api.createStage(project._id, { title: part.title, colour: part.colour }))
    .then(async function (newStage) {
      logAction(
        { commit, state },
        {
          project: project,
          object: newStage,
          reason: 'Created Stage from Part for embedding',
          update: { source: part._id },
        }
      );

      commit(types.setPartProperties, {
        updatePart: part,
        newPartProperties: {
          title: part.title + ' 1',
          colour: colourHelpers.shadeColour(part.colour, 0.4),
        },
      });
      logAction({ commit, state }, { project: project, object: part, reason: 'Embedded Part in Stage' });
      return Promise.all([api.savePart(part), Promise.resolve(newStage)]);
    })
    .then(async ([savedPart, newStage]) => {
      commit(types.setPartProperties, { updatePart: part, newPartProperties: savedPart });

      commit(types.appendUndo, {
        action: 'embedPartInStage',
        data: { part, oldTitle, stage: newStage },
        undoStrategy: undoStrategy,
      });

      var parent = treeHelpers.findParent(project, part._id).children;

      log.debug('actions:embedPartInStage', [parent, newStage, part]);
      commit(types.addStage, { parent: parent, newStage: newStage, index: treeHelpers.indexOfChild(parent, part._id) });
      commit(types.deletePart, { parent: parent, index: treeHelpers.indexOfChild(parent, part._id) });
      commit(types.addPart, {
        parent: treeHelpers.findChildren(project.children, newStage._id),
        newPart: part,
        index: 0,
      });

      await api.saveProject(project).then((savedProject) => {
        commit(types.setProjectProperties, { updateProject: project, newProjectProperties: savedProject });
      });
      return part;
    })
    .catch((e) => {
      log.error('partActions:embaedPartInStage', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const undoAddPart = async ({ commit, state, dispatch }, undo) => {
  var part = state.parts[undo.part._id];
  await dispatch('savePartProperties', {
    updatePart: part,
    newPartProperties: { archived: true },
    undoStrategy: 'undo',
  });
  await dispatch('deletePart', { part: part, undoStrategy: 'undo' });
  // commit(types.deletePart, {parent: oldParent, index: oldIndex});
};

export const redoAddPart = async ({ commit, state, dispatch }, redo) => {
  var project = state.currentProject;

  var part = state.parts[redo.part._id];
  await dispatch('savePartProperties', {
    updatePart: part,
    newPartProperties: { archived: false },
    undoStrategy: 'undo',
  });
  // await dispatch('addPart', undo.part);
  commit(types.addPart, { newPart: part, parent: treeHelpers.findChildInTree(project, redo.parent._id).children });
  await api.saveProject(project).then((savedProject) => {
    var update = { updateProject: project, newProjectProperties: savedProject };
    commit(types.setProjectProperties, update);
  });
};

export const addPart = ({ commit, state }, { siblingPart, parent, newPartProperties, undoStrategy }) => {
  log.debug('actions:addPart', newPartProperties);

  if (!editingAllowed(state)) {
    return;
  }

  var project = state.currentProject;

  if (siblingPart) {
    parent = treeHelpers.findParent(project, siblingPart);
  }

  if (!newPartProperties) {
    newPartProperties = {};
  }
  if (!newPartProperties.hasOwnProperty('controldate')) {
    newPartProperties.controldate = newPartProperties.startdate;
  }

  return api
    .createPart(project._id, newPartProperties)
    .then(async (part) => {
      // setPartProperties({commit, state}, {updatePart: part, newPartProperties: newPartProperties});
      commit(types.addPart, { newPart: part, parent: treeHelpers.findChildInTree(project, parent._id).children });

      commit(types.appendUndo, { action: 'addPart', data: { part, parent: parent }, undoStrategy: undoStrategy });

      logAction({ commit, state }, { project: project, object: part, reason: 'Added Part' });

      await api
        .saveProject(project)
        .then((savedProject) => {
          var update = { updateProject: project, newProjectProperties: savedProject };
          commit(types.setProjectProperties, update);
        })
        .catch((e) => {
          log.error('partActions:addPart', e);
          commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
        });

      return part;
    })
    .catch((e) => {
      log.error('partActions:addPart', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addSyncedPart = ({ commit, state }, part) => {
  commit(types.addPart, { newPart: part });
};

export const undoSavePartProperties = async ({ commit, state, dispatch }, update) => {
  var undoProperties = update.newPartProperties;
  for (var property in undoProperties) {
    undoProperties[property] = update.updatePart[property];
  }

  var part = state.parts[update.updatePart._id];
  await dispatch('savePartProperties', { updatePart: part, newPartProperties: undoProperties, undoStrategy: 'undo' });
};

export const redoSavePartProperties = async ({ commit, state, dispatch }, update) => {
  var part = state.parts[update.updatePart._id];
  await dispatch('savePartProperties', {
    updatePart: part,
    newPartProperties: update.newPartProperties,
    undoStrategy: 'redo',
  });
};

export const savePartProperties = ({ commit, state }, update) => {
  log.debug('actions:savePartProperties', update);

  if (!editingAllowed(state)) {
    return Promise.resolve();
  }

  recalculatePartProperties(update);

  commit(types.appendUndo, { action: 'savePartProperties', data: update, undoStrategy: update.undoStrategy });

  logAction(
    { commit, state },
    {
      project: state.currentProject,
      object: update.updatePart,
      reason: 'Updated Part Properties',
      update: update.newPartProperties,
    }
  );
  setPartProperties({ commit, state }, update);

  return api
    .savePart(update.updatePart)
    .then((savedPart) => {
      update.newPartProperties = savedPart;

      commit(types.setPartProperties, update);
      return update.updatePart;
    })
    .catch((e) => {
      log.error('Error when saving part', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addPartInspection = async ({ dispatch, state, commit, getters }, { part, inspectionTemplateId }) => {
  if (!part.inspectionId) {
    const currentProject = state.currentProject;
    const participants = [];
    currentProject.projectSubcontractors.forEach((participant) => {
      if (!participant.personId) {
        return;
      }

      const person = getters.getPerson(participant.personId);
      if (!person || !person.email || participants.some(({ personId }) => personId === person._id)) {
        return;
      }

      const company = getters.getCompany(person.companyId);
      participants.push({
        personId: person._id,
        name: [person.firstName, person.lastName].filter(Boolean).join(' '),
        firstName: person.firstName,
        lastName: person.lastName,
        companyId: company ? company._id : '',
        companyName: company ? company.companyName : '',
        email: person.email,
        participates: true,
      });
    });

    const projectAddress = currentProject.address || {};
    const inspection = await dispatch('addInspection', {
      projectId: part.projectId,
      address: {
        city: projectAddress.city,
        street: projectAddress.street,
        zipCode: projectAddress.zipCode,
      },
      partId: part._id,
      beginTime: moment(part.startdate).hour(10).toISOString(),
      endTime: moment(part.startdate).hour(11).toISOString(),
      participants,
      inspectionTemplateId,
    });

    commit(types.setSelectedPartInspection, inspection);

    return dispatch('savePartProperties', {
      updatePart: part,
      newPartProperties: {
        inspectionId: inspection._id,
      },
    }).then(() => inspection);
  }

  return dispatch('saveInspectionProperties', {
    updateInspection: state.selectedPartInspection,
    newInspectionProperties: {
      inspectionTemplateId,
      title: getters.currentProjectInspectionTemplatesById(inspectionTemplateId).title,
    },
  });
};

export const syncPartProperties = ({ commit, state }, update) => {
  commit(types.setPartProperties, update);
};

export const setPartProperties = ({ commit, state }, update) => {
  if (!editingAllowed(state)) {
    return;
  }

  commit(types.setPartProperties, update);
};



// WEBPACK FOOTER //
// ./src/store/part/partActions.js